import { useEffect, useState } from "react";
import type { ToastItem } from "../stores/toastStore";

interface Props {
  toasts: ToastItem[];
  onRemove: (id: number) => void;
}

const STYLES = {
  success: {
    bg: "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800",
    text: "text-green-700 dark:text-green-300",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>,
  },
  error: {
    bg: "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800",
    text: "text-red-700 dark:text-red-300",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></svg>,
  },
  info: {
    bg: "bg-indigo-50 dark:bg-indigo-950 border-indigo-200 dark:border-indigo-800",
    text: "text-indigo-700 dark:text-indigo-300",
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>,
  },
};

function ToastMessage({ toast, onRemove }: { toast: ToastItem; onRemove: () => void }) {
  const [visible, setVisible] = useState(false);
  const style = STYLES[toast.type];

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onRemove, 300);
    }, 3000);
    return () => clearTimeout(timer);
    // onRemove는 부모가 인라인 화살표 함수로 전달하므로 렌더마다 참조가 바뀜 — deps 추가 시 타이머 재설정 루프 위험
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`flex items-center gap-3 px-5 py-3.5 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300 ${style.bg} ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
    >
      <span className="shrink-0">{style.icon}</span>
      <span className={`text-sm font-medium ${style.text}`}>{toast.message}</span>
      <button onClick={onRemove} className="ml-auto shrink-0 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 cursor-pointer">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 6-12 12" /><path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function Toast({ toasts, onRemove }: Props) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 w-[90%] max-w-md">
      {toasts.map((t) => (
        <ToastMessage key={t.id} toast={t} onRemove={() => onRemove(t.id)} />
      ))}
    </div>
  );
}
